<script lang="ts">
  import { messages, type Conversation, type Inbox } from '../../stores/messages';
  import { onMount } from 'svelte';

  interface Props {
    selectedId?: string;
    onSelect?: (conversation: Conversation) => void;
    onNewMessage?: () => void;
  }

  let { selectedId, onSelect, onNewMessage }: Props = $props();

  let inbox: Inbox = $state('accepted');

  onMount(() => {
    messages.fetchConversations('accepted');
    messages.fetchPendingRequestsCount();
  });

  function switchInbox(next: Inbox) {
    inbox = next;
    messages.fetchConversations(next);
  }

  function formatTime(dateString?: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return '어제';
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  }

  function tabClass(target: Inbox): string {
    return inbox === target
      ? 'border-b-2 border-primary text-primary font-semibold'
      : 'text-content-tertiary hover:text-content';
  }
</script>

<div class="h-full flex flex-col">
  <div class="p-4 border-b border-default bg-surface flex items-center justify-between gap-2">
    <h2 class="text-h2 text-content">메시지</h2>
    {#if onNewMessage}
      <button
        type="button"
        class="btn btn-primary btn-sm flex items-center gap-1"
        onclick={() => onNewMessage?.()}
        aria-label="새 메시지"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        새 메시지
      </button>
    {/if}
  </div>

  <div class="flex border-b border-default bg-surface">
    <button type="button" class="flex-1 px-3 py-2 text-body2 transition-colors {tabClass('accepted')}" onclick={() => switchInbox('accepted')}>
      메시지함
    </button>
    <button type="button" class="flex-1 px-3 py-2 text-body2 transition-colors {tabClass('requests')}" onclick={() => switchInbox('requests')}>
      받은 요청{#if $messages.pendingRequestsCount > 0}
        <span class="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-error text-white text-[10px] font-semibold">
          {$messages.pendingRequestsCount > 9 ? '9+' : $messages.pendingRequestsCount}
        </span>
      {/if}
    </button>
  </div>

  <div class="flex-1 overflow-y-auto">
    {#if $messages.isConversationsLoading && $messages.conversations.length === 0}
      <div class="p-4 space-y-4">
        {#each Array(5) as _}
          <div class="animate-pulse flex items-center gap-3">
            <div class="w-12 h-12 bg-gray-200 dark:bg-dark-tertiary rounded-full"></div>
            <div class="flex-1">
              <div class="h-4 bg-gray-200 dark:bg-dark-tertiary rounded w-1/2 mb-2"></div>
              <div class="h-3 bg-gray-200 dark:bg-dark-tertiary rounded w-3/4"></div>
            </div>
          </div>
        {/each}
      </div>
    {:else if $messages.conversations.length === 0}
      <div class="p-8 text-center text-content-tertiary">
        <svg class="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <p>{inbox === 'requests' ? '받은 요청이 없습니다' : '아직 메시지가 없습니다'}</p>
      </div>
    {:else}
      {#each $messages.conversations as conversation (conversation.id)}
        <button
          onclick={() => onSelect?.(conversation)}
          class="w-full flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-dark-tertiary transition-colors text-left
                 {selectedId === conversation.id ? 'bg-primary-50 dark:bg-primary/10' : ''}"
        >
          <div class="relative flex-shrink-0">
            <div class="w-12 h-12 bg-primary-50 dark:bg-dark-tertiary rounded-full
                        flex items-center justify-center text-primary dark:text-dark-text-high font-semibold">
              {conversation.other_participant.name.charAt(0).toUpperCase()}
            </div>
            {#if conversation.unread_count > 0}
              <span class="absolute -top-1 -right-1 w-5 h-5 bg-error text-white text-xs
                           rounded-full flex items-center justify-center font-semibold">
                {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
              </span>
            {/if}
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2">
              <span class="font-semibold truncate {conversation.unread_count > 0 ? 'text-content' : 'text-gray-700 dark:text-dark-text-medium'}">
                {conversation.other_participant.name}
              </span>
              <span class="text-caption text-content-tertiary flex-shrink-0">
                {formatTime(conversation.last_message_at)}
              </span>
            </div>
            <p class="text-body2 truncate {conversation.unread_count > 0 ? 'text-gray-700 dark:text-dark-text-medium' : 'text-content-tertiary'}">
              {#if conversation.status === 'pending' && !conversation.is_initiator}
                <span class="text-warning font-medium">새 요청 · </span>
              {/if}{conversation.last_message_preview || '메시지 없음'}
            </p>
          </div>
        </button>
      {/each}
    {/if}
  </div>
</div>
